import { CustomerAddress } from '@porterin/booking-flow/src/order-review/domain/entities/CustomerAddress';
import { getDefaultExtAddressDetails } from '@porterin/booking-flow/src/order-review/domain/entities/ExtendedAddress';
import { Waypoint } from '@porterin/booking-flow/src/order-review/domain/entities/Waypoint';
import { FormData as addressFormData } from '@porterin/booking-flow/src/order-review/view/types/OrderReviewAddressSchema';
import { QueryMapperResult } from '../../domain/types/HelperTypes';
import {
  FareEstimateForm,
  FormType,
} from '../../domain/entities/FareEstimateForm';
import {
  fare_retry_source,
  FareEstimateCategory,
} from '../../domain/store/FareEstimateStore.store';
import { publishEvents } from '@porterin/component-library/src/ts-utils/analytics/react-ga/EventTracker';
import { fireGTEEventsOnLeadCreated } from '../../domain/helpers/FareEstimateEventHelper';
import { FeParamsPage } from '@porterin/component-library/src/ts-utils/analytics/configs/FeParamsPage';
import { saveToStoreAndRedirect } from '@porterin/component-library/src/ts-utils/analytics/helpers/FeParamsHelper';
import {
  ga_fare_estimate_form_validation,
  service_category_trucks,
  ga_event_2w,
  ga_fare_estimate_successful_2w,
  ga_fare_estimate_successful,
  service_category_two_wheelers,
} from '../../domain/configs/Store.store';
import { submitFareEstimateSecure } from '../../domain/repo/FareEstimateSecureRepo';
import { submitFareEstimate } from '../../domain/repo/FareEstimateValidatorRepo';
import {
  getUtmParams,
  parseUtmParams,
} from '../../components/FareEstimateUtmParamsHelper';
import {
  Actions,
  ClientData,
} from '../../reducers/fare-estimate-results/FareEstimateResultsReducerTypes.type';
import {
  DefaultLocales,
  Locales,
} from '@porterin/component-library/src/ts-utils/analytics/constants/LocaleConstants';
import {
  constructRedirectUrl,
  getCategoryPayload,
  stringToFormType,
} from '../../components/FareEstimateHelper';
import { FareEstimateSecureError } from '../../domain/entities/FareEstimateSecureError';
import { FareEstimateError } from '../../domain/entities/FareEstimateValidatorError';
import { BookingDetailsWithoutAddress } from '../../domain/entities/fare-estimate-results/EditFareResultsFormTypes.type';
import { captureMessage } from '@sentry/react';
import { VehicleImage } from 'libs/booking-flow/src/order-review/domain/entities/VehicleResult';
import { VehicleDetails } from 'libs/booking-flow/src/order-review/view/types/VehicleDetails';
import { FareEstimateResults } from '../../domain/entities/fare-estimate-results/FareEstimateResults.type';
import { VehiclesCms } from '../../domain/entities/fare-estimate-results/VehiclesCms.type';
import { DEFAULT_TRUCK_ICON } from '../../domain/store/FareEstimateResultsStore';
import { Dispatch } from 'react';
import { checkCustomerExists } from '@porterin/booking-flow/src/auth/domain/repo/customerAuth';
import { FareEstimateValidator } from '../../domain/entities/FareEstimateValidator';
import {
  RECEIVER_LOCAL_STORAGE_KEY,
  SENDER_LOCAL_STORAGE_KEY,
} from '../../domain/configs/Config';

const getLandmarkLocation = (landmarkLocation?: string): string =>
  (landmarkLocation ?? '').replace(/\+/g, ' ');

const getLocationCoordinates = (locationCoordinates?: string): number => {
  if (!locationCoordinates) return 0.0;
  const parsed = Number(locationCoordinates);
  return Number.isFinite(parsed) ? parsed : 0.0;
};

export const setBookingDetailsInStorage = (
  waypoint: Waypoint,
  data: BookingDetailsWithoutAddress,
): void => {
  const key =
    waypoint === 'Pickup'
      ? SENDER_LOCAL_STORAGE_KEY
      : RECEIVER_LOCAL_STORAGE_KEY;

  localStorage.setItem(key, JSON.stringify(data));
};

const readBookingDetailsFromStorage = (
  waypoint: Waypoint,
): BookingDetailsWithoutAddress | null => {
  const key =
    waypoint === 'Pickup'
      ? SENDER_LOCAL_STORAGE_KEY
      : RECEIVER_LOCAL_STORAGE_KEY;

  const stored = localStorage.getItem(key);
  if (stored === null) {
    return null;
  }

  try {
    return JSON.parse(stored) as BookingDetailsWithoutAddress;
  } catch (err) {
    console.error('Failed to parse booking details from storage', err);
    return null;
  }
};
export function makeCustomerAddressFromClientData(
  clientData: QueryMapperResult,
  waypoint: Waypoint,
): CustomerAddress {
  const bookingDetailsFromStorage = readBookingDetailsFromStorage(waypoint);

  return {
    pickupAddress: {
      landmark: getLandmarkLocation(clientData.from_address_landmark),
      location: {
        lat: getLocationCoordinates(clientData.from_address_lat),
        lng: getLocationCoordinates(clientData.from_address_long),
      },
      addressDetails: getDefaultExtAddressDetails(),
    },
    dropAddress: {
      landmark: getLandmarkLocation(clientData.to_address_landmark),
      location: {
        lat: getLocationCoordinates(clientData.to_address_lat),
        lng: getLocationCoordinates(clientData.to_address_long),
      },
      addressDetails: getDefaultExtAddressDetails(),
    },
    mobile:
      bookingDetailsFromStorage?.mobile ??
      (waypoint === 'Pickup'
        ? clientData.sender_mobile
        : clientData.customer_mobile),
    name:
      bookingDetailsFromStorage?.name ??
      (waypoint === 'Pickup'
        ? clientData.sender_name
        : clientData.customer_name),
    house: bookingDetailsFromStorage?.house,
  };
}

export function convertToFareEstimateApiInput(
  form_data: addressFormData,
  client_data: ClientData,
): FareEstimateForm {
  return {
    name: client_data.customer_name ?? '',
    mobile: client_data.customer_mobile ?? '',
    requirement: client_data.frequency === 'business' ? 'business' : 'personal',
    pickupAddress: {
      landmark: form_data.pickupAddress.landmark,
      location: form_data.pickupAddress.location,
    },
    dropAddress: {
      landmark: form_data.dropAddress.landmark,
      location: form_data.dropAddress.location,
    },
    serviceCategory: getCategoryPayload(
      stringToFormType(client_data.service_category ?? ''),
    ),
  };
}

export const getAddressErrorMessage = (
  error: FareEstimateSecureError | FareEstimateError,
): string => {
  if ('status' in error) return error.error;

  return (
    error?.fieldErrors?.pickup ??
    error?.fieldErrors?.drop ??
    error.formError ??
    'An unexpected error occurred'
  );
};

export async function submitEditFareResultsFormData(
  address_form_data: addressFormData,
  client_data: ClientData,
  success_callback?: () => void,
  error_callback?: (error: FareEstimateSecureError | FareEstimateError) => void,
  category: string = FareEstimateCategory,
  is_bkf_enabled: boolean = false,
): Promise<void> {
  const formData = convertToFareEstimateApiInput(
    address_form_data,
    client_data,
  );
  const service_category = stringToFormType(client_data.service_category ?? '');

  publishEvents(
    category,
    ga_fare_estimate_form_validation,
    `${service_category === FormType.Trucks ? service_category_trucks : ga_event_2w}_${formData.mobile}`,
  );
  const fareEstimateValidatorResult = await submitFareEstimate(formData);
  const utmObject = parseUtmParams(getUtmParams());

  if ('redirectUrl' in fareEstimateValidatorResult) {
    const geoRegionIdParam =
      new URL(fareEstimateValidatorResult.redirectUrl).searchParams.get(
        'geo_region_id',
      ) ?? undefined;

    const newFormData = {
      ...formData,
      geoRegionId: geoRegionIdParam,
    };

    const token = await submitFareEstimateSecure(newFormData);

    if ('token' in token && typeof token.token === 'string') {
      const { token: secureToken } = token;
      const finalRedirectUrl = constructRedirectUrl(
        fareEstimateValidatorResult,
        service_category,
        secureToken,
        false,
        formData,
        is_bkf_enabled,
      );
      const nonTruckEvent =
        service_category === FormType.TwoWheeler
          ? ga_fare_estimate_successful_2w
          : ga_fare_estimate_successful;
      const event =
        service_category === FormType.Trucks
          ? service_category_trucks
          : nonTruckEvent;
      publishFareRetryEvents(
        category,
        event,
        formData,
        utmObject,
        service_category,
        fareEstimateValidatorResult,
      );
      success_callback?.();
      saveToStoreAndRedirect(
        finalRedirectUrl,
        FeParamsPage.FareEstimateResults,
      );
    } else if ('error' in token) {
      error_callback?.(token);
    }
  } else {
    error_callback?.(fareEstimateValidatorResult);
  }
}

const getRelatedVehicleIcon = (
  vehicle_id: number,
  vehicles_cms: VehiclesCms,
): VehicleImage => {
  const vehicle = vehicles_cms.vehicles.find(
    vehicle_cms => vehicle_cms.vehicle_id === vehicle_id,
  );
  if (!vehicle) {
    captureMessage(
      `Fare Estimate - Icon not found for vehicle_id ${vehicle_id}`,
      {
        tags: {
          page: 'Fare Estimate Results',
          vehicle_id: vehicle_id,
        },
      },
    );
    return DEFAULT_TRUCK_ICON;
  }
  return {
    path: vehicle.vehicle_image.path,
    altText: vehicle.vehicle_image.alt_text,
  };
};

export const toVehicleVm = (
  fareEstimateResults: FareEstimateResults,
  vehicles_cms: VehiclesCms,
): VehicleDetails[] => {
  return fareEstimateResults.vehicles.map((vehicle): VehicleDetails => {
    const relatedIcon = getRelatedVehicleIcon(vehicle.vehicleId, vehicles_cms);

    return {
      vehicleId: vehicle.vehicleId,
      name: vehicle.name,
      capacity: vehicle.capacityDisplay,
      fare: vehicle.baseFare,
      image: relatedIcon,
      selectedImage: relatedIcon,
    };
  });
};

export const checkCustomerExistDispatch = (
  mobile: string,
  dispatch: Dispatch<Actions>,
) => {
  checkCustomerExists(mobile)
    .then(customerExistResult => {
      dispatch({
        type: 'SetDoesCustomerExist',
        payload: customerExistResult.exists,
      });
    })
    .catch(_error => {
      dispatch({
        type: 'SetDoesCustomerExist',
        payload: null,
      });
    });
};

function publishFareRetryEvents(
  category: string,
  event: string,
  form_data: FareEstimateForm,
  utm_params: Record<string, string>,
  service_category: FormType,
  result: FareEstimateValidator,
) {
  publishEvents(
    category,
    event,
    `1_${form_data.name}#2_${form_data.mobile}#3_frequency-${form_data.requirement}#4_${utm_params['utm_source']}#5_${utm_params['utm_campaign']}#6_${service_category}`,
  );
  if (result.callbackRequestData.leadEntryCreated) {
    fireGTEEventsOnLeadCreated(
      result.redirectUrl,
      service_category === FormType.Trucks
        ? service_category_trucks
        : service_category_two_wheelers,
      category,
      fare_retry_source,
      result.callbackRequestData.sendUnregisteredEvent ?? false,
    );
  }
}

export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};
